import React, { useState, useEffect, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import axios from "axios";
import config from "../../config.js";
import Update_Icon from "../../images/table/Update_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import { usePermission } from "../../hooks/usePermission";
import { CircularProgress } from "@mui/material";
import DeleteModal from "../common/DeleteModal";
import { useTranslation } from "react-i18next";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import { formatDate, formatTime } from "../../utils/formatDate";
import { getScheduleOccurrenceDetails } from "../../utils/scheduleUi";
const DynamicTable = React.lazy(() => import("../common/DynamicTable"));
const headers = [
  { key: "Name", value: "Schedules" },
  { key: "Description", value: "Description" },
  { key: "Start Date", value: "Start Date" },
  { key: "Start Time", value: "Start Time" },
  { key: "End Time", value: "End Time" },
  { key: "Repetition", value: "Repetition" },
  { key: "Route", value: "Route" },
  { key: "Vehicle", value: "Vehicle" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

const customActions = [
  // {
  //     id: 'view',
  //     label: 'View',
  //     icon: view_Icon,
  //     path: '/schedule'
  // },
  {
    id: "update",
    label: "Update",
    icon: Update_Icon,
    path: "/schedule",
  },
  {
    id: "delete",
    label: "Delete",
    className: "delete",
    icon: Delete_Icon,
    path: "",
  },
];

export default function ScheduleMain() {
  const { t } = useTranslation();
  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;
  const [scheduleList, setScheduleList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    startTime: "",
    repetition: "",
    routeName: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [nextHourSchedules, setNextHourSchedules] = useState(0);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const formatRepetition = (repetition) => {
    if (!repetition) return "N/A";

    // Handle the special case for "ONETIME"
    if (repetition.toUpperCase() === "ONETIME") {
      return "One time";
    }

    // Convert "DAILY" to "Daily", "WEEKLY" to "Weekly", etc.
    return (
      repetition.charAt(0).toUpperCase() + repetition.slice(1).toLowerCase()
    );
  };

  // Format ISO date string to 12-hour time with AM/PM (for startTime and endTime)
  // Format ISO date string to include both date and time in 12-hour format with AM/PM
  const formatIsoTime = (isoTimeStr) => {
    if (!isoTimeStr) return "N/A";
    const date = new Date(isoTimeStr);
    if (isNaN(date.getTime())) return "Invalid Date";
    const datePart = date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const timePart = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    // return `${datePart} ${timePart}`;
    return `${timePart}`;
  };

  // Format "HH:mm" time string to 12-hour time with AM/PM (for arrivalTime and departureTime)
  const formatTimeOnly = (timeStr) => {
    if (!timeStr) return "N/A";
    const [hours, minutes] = timeStr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return "Invalid Time";
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Define filter configuration
  const [filterConfig, setFilterConfig] = useState([
    {
      id: "repetition", // Fixed typo: repitation -> repetition
      label: "Repetition",
      helperText: "Select repetition type",
      options: [
        { value: "DAILY", label: "Daily" },
        { value: "WEEKLY", label: "Weekly" },
        { value: "MONTHLY", label: "Monthly" },
        { value: "ONETIME", label: "One time" },
      ],
    },
    {
      id: "startTime",
      label: "Start Time",
      helperText: "Select start time",
      options: [],
    },
    {
      id: "routeName",
      label: "Routes",
      helperText: "Select route",
      options: [],
    },
  ]);

  // Function to fetch all schedules
  async function getAllSchedules() {
    if (!orgId) return [];

    setIsLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/schedule/organization`, {
        params: { organizationId: orgId },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.log("Error in fetching schedule data: ", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  // Function to delete schedule
  async function deleteSchedule(id) {
    try {
      const response = await axios.delete(`${config.baseURL}/schedule`, {
        data: { scheduleId: Number(id) },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.log("Error in deleting schedule data: ", error);
      throw error;
    }
  }

  // Fetch schedules data on component mount
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const schedules = await getAllSchedules();
        setScheduleList(schedules);

        // Calculate schedules in next hour
        const now = new Date();
        const nextHour = new Date(now.getTime() + 60 * 60 * 1000);
        const schedulesInNextHour = schedules.filter((schedule) => {
          if (schedule.scheduleStatus === "RUNNING") {
            return true;
          }

          const occurrenceStart = getScheduleOccurrenceDetails(schedule).sortValue;

          if (!occurrenceStart) {
            return false;
          }

          const scheduleTime = new Date(occurrenceStart);
          return !Number.isNaN(scheduleTime.getTime()) && scheduleTime >= now && scheduleTime <= nextHour;
        });
        setNextHourSchedules(schedulesInNextHour.length);

        // Extract unique start times for filter
        const startTimes = [];
        const startTimeSet = new Set();
        schedules.forEach((schedule) => {
          if (schedule.startTime && !startTimeSet.has(schedule.startTime)) {
            startTimeSet.add(schedule.startTime);
            startTimes.push({
              value: schedule.startTime,
              label: formatIsoTime(schedule.startTime),
            });
          }
        });

        // Extract unique routes for filter
        const routes = [];
        const routeSet = new Set();
        schedules.forEach((schedule) => {
          if (
            schedule.route &&
            schedule.route.name &&
            !routeSet.has(schedule.route.name)
          ) {
            routeSet.add(schedule.route.name);
            routes.push({
              value: schedule.route.name,
              label: schedule.route.name,
            });
          }
        });

        // Update filter config with dynamic options
        setFilterConfig((prev) => {
          const updated = [...prev];
          const startTimeIndex = updated.findIndex((f) => f.id === "startTime");
          const routeNameIndex = updated.findIndex((f) => f.id === "routeName");

          if (startTimeIndex >= 0) {
            updated[startTimeIndex].options = startTimes;
          }

          if (routeNameIndex >= 0) {
            updated[routeNameIndex].options = routes;
          }

          return updated;
        });
      } catch (error) {
        console.error("Error fetching schedule data:", error);
      }
    };

    fetchSchedules();
  }, [navigate, orgId]);

  // Format data for table display
  const formatDataForTable = (data) => {
    return data.map((schedule) => {
      const formattedData = {
        Name: schedule.name,
        Description: schedule.description,
        "Start Date": formatDate(schedule.startTime, false, false),
        "Start Time": formatTime(schedule.startTime),
        "End Time": formatTime(schedule.endTime),
        Repetition: formatRepetition(
          schedule.repetition || schedule.repitation
        ),
        Route: schedule.route ? schedule.route.name : "N/A",
        Vehicle: schedule.vehicleSchedule
          ? schedule.vehicleSchedule.map((vs) => vs.vehicle.name).join(", ")
          : "N/A",
      };

      // Add original ID for action handling
      formattedData.id = schedule.id;
      formattedData.originalData = schedule;

      return formattedData;
    });
  };

  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  // Handle filter changes
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };

  // Filter data based on search query and filters
  const filteredScheduleData = scheduleList.filter((schedule) => {
    // Get the repetition value (handle typo from backend)
    const repetitionValue = schedule.repetition || schedule.repitation;

    // Check if repetition filter is applied
    if (filters.repetition && repetitionValue !== filters.repetition) {
      return false;
    }

    // Check if start time filter is applied
    if (filters.startTime && schedule.startTime !== filters.startTime) {
      return false;
    }

    // Check if route name filter is applied
    if (
      filters.routeName &&
      schedule.route &&
      schedule.route.name !== filters.routeName
    ) {
      return false;
    }

    // If no search query, return true (show all that match filters)
    if (!searchQuery) {
      return true;
    }

    // Otherwise, search in all relevant fields
    const query = searchQuery.toLowerCase();
    const startDateStr = formatDate(schedule.startTime, false, false) || "";
    const startTimeStr = formatTime(schedule.startTime) || "";
    const endTimeStr = formatTime(schedule.endTime) || "";
    const vehicleStr = schedule.vehicleSchedule
      ? schedule.vehicleSchedule.map((vs) => vs.vehicle.name).join(", ")
      : "";
    return (
      (schedule.name && schedule.name.toLowerCase().includes(query)) ||
      (schedule.description &&
        schedule.description.toLowerCase().includes(query)) ||
      startDateStr.toLowerCase().includes(query) ||
      startTimeStr.toLowerCase().includes(query) ||
      endTimeStr.toLowerCase().includes(query) ||
      (repetitionValue && formatRepetition(repetitionValue).toLowerCase().includes(query)) ||
      (schedule.route &&
        schedule.route.name &&
        schedule.route.name.toLowerCase().includes(query)) ||
      vehicleStr.toLowerCase().includes(query)
    );
  });

  // Add schedule handler
  const handleAddSchedule = () => {
    if (!hasPermission("/api/schedule", "POST")) {
      return;
    }
    navigate("/schedule/add");
  };

  const handleActionClick = (actionId, rowIndex, actionObj) => {
    const schedule =
      filteredScheduleData[rowIndex].originalData ||
      filteredScheduleData[rowIndex];

    switch (actionId) {
      // case "view":
      //     // Navigate to view mode (read-only)
      //     navigate(`/schedule/${schedule.id}`, { state: { mode: 'view' } });
      //     break;
      case "update":
        if (hasPermission("/api/schedule", "PUT")) {
          navigate(`/schedule/${schedule.id}`);
        }
        break;
      case "delete":
        if (hasPermission("/api/schedule", "DELETE")) {
          setScheduleToDelete(schedule);
          setDeleteModalOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      if (scheduleToDelete) {
        console.log("Deleting schedule ID:", scheduleToDelete.id);
        await deleteSchedule(scheduleToDelete.id);
        setScheduleList((prevList) =>
          prevList.filter((schedule) => schedule.id !== scheduleToDelete.id)
        );
      }
    } catch (error) {
      console.error("Error deleting schedule:", error);
    } finally {
      setDeleteModalOpen(false);
      setScheduleToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setScheduleToDelete(null);
  };

  const allowedActions = [];
  // View is set as available to everyone (no permission check needed for viewing)
  // allowedActions.push(customActions.find(action => action.id === 'view'));

  if (hasPermission("/api/schedule", "PUT")) {
    allowedActions.push(customActions.find((action) => action.id === "update"));
  }
  if (hasPermission("/api/schedule", "DELETE")) {
    allowedActions.push(customActions.find((action) => action.id === "delete"));
  }

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  return (
    <div style={{ minHeight: "100vh" }}>
      <SubTitle
        title="schedules"
        path={`${orgName || "organization"} > schedules`}
        add={hasPermission("/api/schedule", "POST") ? "createSchedule" : null}
        addPath="/schedule/add"
        onAddClick={
          hasPermission("/api/schedule", "POST") ? handleAddSchedule : null
        }
      />

      <Cards
        cardData={[
          {
            title: "totalSchedules",
            value: scheduleList?.length || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "tripsinNext1Hour",
            value: nextHourSchedules,
            backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
        ]}
      />

      <div className="table_container">
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <Suspense fallback={<CircularProgress />}>
          <DynamicTable
            headers={headers}
            data={formatDataForTable(filteredScheduleData || [])}
            onActionClick={handleActionClick}
            actions={allowedActions}
            loading={isLoading}
          />
        </Suspense>
      </div>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Schedule!"
        message={`${t("Are you sure you want to delete")} ${
          scheduleToDelete?.name || "this schedule"
        }? ${t("This action cannot be undone.")}`}
      />
    </div>
  );
}
